/**
 * 物件マスタ (HubSpot の物件カスタムオブジェクト `2-32744235` の写し)。
 *
 * この app が避難マップを作る物件は「既設または設置確定」= 必ず HubSpot にある。
 * つまりマスタの `type` は推定値ではなく**正解表**として扱える
 * (実測: 物件名からの推定62% → マスタ照合94%)。
 *
 * 重要 — 同梱してはいけない:
 *   `src/data/*.json` や `public/data/` に置くとバンドル/静的配信され、
 *   ログイン前でも11,000件超の顧客物件リストが取得できてしまう。
 *   マスタは Firestore (`building_master`) にのみ置き、ログイン後に読む。
 *   生成は scripts/import-hubspot-buildings.mjs (出力先もリポジトリ外)。
 */
import type { LngLat, PropertyKind } from './property';

/** HubSpot 側の賃貸/分譲区分 (レジデンスのみ)。 */
export type ResidenceTenure = 'rental' | 'condo';

export interface BuildingMasterEntry {
  /**
   * HubSpot の物件レコードID (hs_object_id)。
   * 物件名は重複する (707組を検出) ため、紐付けの実体は常にこのIDにする。
   */
  id: string;
  /** 物件名。表示と検索に使う。同名が存在しうるので単独のキーにはしない。 */
  name: string;
  /** 物件種別。HubSpot の「外部端末」は避難マップの対象外なので undefined。 */
  type?: PropertyKind;
  residenceTenure?: ResidenceTenure;
  prefecture?: string;
  municipality?: string;
  /** 番地以下。HubSpot 側のフィールド名は綴り誤り (buiilding_address_detail)。 */
  addressDetail?: string;
  postalCode?: string;
  /** 階数。HubSpot 側はほぼ未入力 (実測0.1%) なので基本 undefined。 */
  floors?: number;

  /**
   * 取り込み時に解決済みの座標。実行時にジオコーディングしないための焼き込み。
   * 住所が無い物件 (実測2,638件) には入らないので、その場合は従来どおり
   * 住所入力 → ジオコーディングの経路に落ちる。
   */
  coords?: LngLat;
  /** ジオコーダが返した正規化住所。表示と突き合わせに使う。 */
  normalizedAddress?: string;
  /**
   * 座標の確からしさ。2つのジオコーダ (Mapbox Search Box / 国土地理院) の
   * 結果が近ければ verified、離れていれば要確認。片方しか引けなければ single。
   * 住所の入力ミスは片寄せでは検出できないので、突き合わせで炙り出す。
   */
  geocodeStatus?: 'verified' | 'check' | 'single';
  /** 2つのジオコーダの距離差 (m)。check の判断根拠。 */
  geocodeGapMeters?: number;

  /** 取り込み日時 (ISO)。 */
  updatedAt: string;
}

/** 表示・ジオコーディング用の住所文字列。欠けている部分は詰める。 */
export function buildingAddress(b: BuildingMasterEntry): string {
  return [b.prefecture, b.municipality, b.addressDetail].filter(Boolean).join('');
}

/** 住所が「市区町村まで入っている」か (入力ミス「東京都あ-」等を弾く)。 */
export function hasUsableAddress(b: BuildingMasterEntry): boolean {
  const m = b.municipality ?? '';
  return m.length >= 2 && /[区市町村郡]/.test(m);
}
